import { z } from "zod";
import { CLAVES } from "../sat/catalogos.js";
/**
 * El CFDI 4.0 tal como se manda al PAC.
 *
 * Los nombres son PascalCase a propósito: son los del Anexo 20 y así viajan
 * en el JSON de Factura Digital. Este es el ÚNICO lugar del código donde se
 * usa esa convención — del lado del dominio todo es camelCase. Si algún día
 * cambiamos de PAC, este archivo es el que se reescribe.
 *
 * Todos los importes son string, no number. Ver money.ts.
 */
/** Importe monetario: string con punto decimal y hasta 6 decimales. */
const importe = z
    .string()
    .regex(/^-?\d+(\.\d{1,6})?$/, "importe con formato inválido");
const rfc = z
    .string()
    .regex(/^([A-ZÑ&]{3,4})\d{6}([A-Z\d]{3})$/, "RFC con estructura inválida");
const codigoPostal = z.string().regex(/^\d{5}$/, "código postal inválido");
export const trasladoSchema = z.object({
    Base: importe,
    Impuesto: z.enum(CLAVES.impuesto),
    TipoFactor: z.enum(CLAVES.tipoFactor),
    TasaOCuota: importe.optional(),
    Importe: importe.optional(),
});
export const retencionSchema = trasladoSchema;
export const impuestosConceptoSchema = z.object({
    Traslados: z.array(trasladoSchema).optional(),
    Retenciones: z.array(retencionSchema).optional(),
});
export const conceptoCfdiSchema = z.object({
    ClaveProdServ: z.string().min(1),
    NoIdentificacion: z.string().max(100).optional(),
    Cantidad: importe,
    ClaveUnidad: z.string().min(1),
    Unidad: z.string().max(20).optional(),
    Descripcion: z.string().min(1).max(1000),
    ValorUnitario: importe,
    Importe: importe,
    Descuento: importe.optional(),
    ObjetoImp: z.enum(CLAVES.objetoImp),
    Impuestos: impuestosConceptoSchema.optional(),
});
export const emisorSchema = z.object({
    // El PAC completa Rfc y Nombre desde la cuenta; los mandamos igual para
    // que un error de configuración truene aquí y no después del timbre.
    Rfc: rfc.optional(),
    Nombre: z.string().optional(),
    RegimenFiscal: z.enum(CLAVES.regimenFiscal),
});
export const receptorSchema = z.object({
    Rfc: rfc,
    /** Debe ir EXACTAMENTE como está en la Constancia de Situación Fiscal. */
    Nombre: z.string().min(1),
    DomicilioFiscalReceptor: codigoPostal,
    RegimenFiscalReceptor: z.enum(CLAVES.regimenFiscal),
    UsoCFDI: z.enum(CLAVES.usoCfdi),
    ResidenciaFiscal: z.string().length(3).optional(),
    NumRegIdTrib: z.string().optional(),
});
export const cfdiRelacionadosSchema = z.object({
    TipoRelacion: z.enum(CLAVES.tipoRelacion),
    CfdiRelacionado: z.array(z.object({ UUID: z.uuid() })).min(1),
});
// --- Complemento de Pagos 2.0 -----------------------------------------------
//
// Sufijos distintos a los de Conceptos/Impuestos a propósito: el Anexo 20
// define BaseP/ImpuestoP/... a nivel Pago y BaseDR/ImpuestoDR/... a nivel
// DoctoRelacionado — no son el mismo nodo que trasladoSchema, aunque tengan
// la misma forma.
//
// ⚠️ No verificado contra el sandbox del PAC todavía (no hay PAC_API_KEY
// configurada). El ejemplo que expone Factura Digital en su documentación
// usa ObjetoImpDR="01" sin ImpuestosDR y solo el agregado TrasladosP a nivel
// Pago; aquí se optó por seguir el Anexo 20 al pie de la letra —incluyendo
// ImpuestosDR por documento cuando aplica— por ser lo que el SAT valida en
// definitiva. Antes de timbrar un pago real, confirmar que el PAC acepta
// esta forma y no solo la simplificada de su ejemplo.
const trasladoPSchema = z.object({
    BaseP: importe,
    ImpuestoP: z.enum(CLAVES.impuesto),
    TipoFactorP: z.enum(CLAVES.tipoFactor),
    TasaOCuotaP: importe.optional(),
    ImporteP: importe.optional(),
});
const retencionPSchema = trasladoPSchema;
const trasladoDRSchema = z.object({
    BaseDR: importe,
    ImpuestoDR: z.enum(CLAVES.impuesto),
    TipoFactorDR: z.enum(CLAVES.tipoFactor),
    TasaOCuotaDR: importe.optional(),
    ImporteDR: importe.optional(),
});
const retencionDRSchema = trasladoDRSchema;
export const doctoRelacionadoSchema = z.object({
    IdDocumento: z.uuid(),
    Serie: z.string().max(25).optional(),
    Folio: z.string().max(40).optional(),
    MonedaDR: z.enum(CLAVES.moneda),
    EquivalenciaDR: importe.optional(),
    NumParcialidad: z.number().int().min(1),
    ImpSaldoAnt: importe,
    ImpPagado: importe,
    ImpSaldoInsoluto: importe,
    ObjetoImpDR: z.enum(CLAVES.objetoImp),
    ImpuestosDR: z
        .object({
        RetencionesDR: z.array(retencionDRSchema).optional(),
        TrasladosDR: z.array(trasladoDRSchema).optional(),
    })
        .optional(),
});
export const pagoCfdiSchema = z.object({
    FechaPago: z.iso.datetime({ local: true }),
    FormaDePagoP: z.enum(CLAVES.formaPago),
    MonedaP: z.enum(CLAVES.moneda),
    TipoCambioP: importe.optional(),
    Monto: importe,
    NumOperacion: z.string().max(100).optional(),
    RfcEmisorCtaOrd: z.string().optional(),
    NomBancoOrdExt: z.string().optional(),
    CtaOrdenante: z.string().max(50).optional(),
    RfcEmisorCtaBen: z.string().optional(),
    CtaBeneficiario: z.string().max(50).optional(),
    DoctoRelacionado: z.array(doctoRelacionadoSchema).min(1),
    /** Anexo 20: los impuestos del pago viven dentro de ImpuestosP, no sueltos. */
    ImpuestosP: z
        .object({
        RetencionesP: z.array(retencionPSchema).optional(),
        TrasladosP: z.array(trasladoPSchema).optional(),
    })
        .optional(),
});
export const pagos20Schema = z.object({
    Totales: z
        .object({
        TotalRetencionesIVA: importe.optional(),
        TotalRetencionesISR: importe.optional(),
        TotalRetencionesIEPS: importe.optional(),
        TotalTrasladosBaseIVA16: importe.optional(),
        TotalTrasladosImpuestoIVA16: importe.optional(),
        TotalTrasladosBaseIVA8: importe.optional(),
        TotalTrasladosImpuestoIVA8: importe.optional(),
        TotalTrasladosBaseIVA0: importe.optional(),
        TotalTrasladosImpuestoIVA0: importe.optional(),
        TotalTrasladosBaseIVAExento: importe.optional(),
        MontoTotalPagos: importe,
    })
        .optional(),
    Pago: z.array(pagoCfdiSchema).min(1),
});
const complementoSchema = z.object({
    Pagos20: pagos20Schema.optional(),
});
export const impuestosComprobanteSchema = z.object({
    TotalImpuestosTrasladados: importe.optional(),
    TotalImpuestosRetenidos: importe.optional(),
    Traslados: z
        .array(z.object({
        Base: importe,
        Impuesto: z.enum(CLAVES.impuesto),
        TipoFactor: z.enum(CLAVES.tipoFactor),
        TasaOCuota: importe.optional(),
        Importe: importe.optional(),
    }))
        .optional(),
    Retenciones: z
        .array(z.object({
        Impuesto: z.enum(CLAVES.impuesto),
        Importe: importe,
    }))
        .optional(),
});
export const comprobanteSchema = z
    .object({
    Version: z.literal("4.0"),
    Serie: z.string().max(25).optional(),
    Folio: z.string().max(40).optional(),
    /** "AUTO" deja que el PAC ponga la fecha; evita el error de 72 horas. */
    Fecha: z.union([z.literal("AUTO"), z.iso.datetime({ local: true })]),
    FormaPago: z.enum(CLAVES.formaPago).optional(),
    CondicionesDePago: z.string().max(1000).nullable().optional(),
    SubTotal: importe,
    Descuento: importe.optional(),
    Moneda: z.enum(CLAVES.moneda),
    TipoCambio: importe.optional(),
    Total: importe,
    TipoDeComprobante: z.enum(CLAVES.tipoComprobante),
    Exportacion: z.enum(CLAVES.exportacion),
    MetodoPago: z.enum(CLAVES.metodoPago).optional(),
    LugarExpedicion: codigoPostal,
    CfdiRelacionados: z.array(cfdiRelacionadosSchema).optional(),
    Emisor: emisorSchema,
    Receptor: receptorSchema,
    Conceptos: z.array(conceptoCfdiSchema).min(1),
    Impuestos: impuestosComprobanteSchema.optional(),
    // Sin este campo, Zod descarta la clave "Complemento" al validar —los
    // object schema quitan por defecto lo que no está declarado— y el nodo
    // de Pagos20 se perdería en silencio justo antes de mandarlo al PAC.
    Complemento: complementoSchema.optional(),
})
    .superRefine((c, ctx) => {
    // Reglas del Anexo 20 que el PAC cobra un timbre por descubrir.
    if (c.Moneda !== "MXN" && c.Moneda !== "XXX" && !c.TipoCambio) {
        ctx.addIssue({
            code: "custom",
            path: ["TipoCambio"],
            message: "TipoCambio es obligatorio cuando la moneda no es MXN",
        });
    }
    if (c.TipoDeComprobante === "I" && !c.MetodoPago) {
        ctx.addIssue({
            code: "custom",
            path: ["MetodoPago"],
            message: "MetodoPago es obligatorio en comprobantes de ingreso",
        });
    }
    if (c.TipoDeComprobante === "P") {
        if (c.Receptor.UsoCFDI !== "CP01") {
            ctx.addIssue({
                code: "custom",
                path: ["Receptor", "UsoCFDI"],
                message: "Los CFDI de pago llevan UsoCFDI CP01",
            });
        }
        // Comparación numérica, no de texto: "0.00" es cero igual que "0", y
        // el mapeo siempre normaliza a los decimales de la moneda (money.ts),
        // así que un comprobante real nunca llega aquí con el string "0" pelón.
        if (Number(c.SubTotal) !== 0 || Number(c.Total) !== 0) {
            ctx.addIssue({
                code: "custom",
                path: ["Total"],
                message: "Los CFDI de pago llevan SubTotal y Total en 0",
            });
        }
    }
});
/** Lo que devuelve el PAC cuando el timbrado sale bien. */
export const timbreSchema = z.object({
    UUID: z.string(),
    FechaTimbrado: z.string(),
    NoCertificado: z.string().optional(),
    NoCertificadoSAT: z.string().optional(),
    RfcProvCertif: z.string().optional(),
    SelloCFD: z.string().optional(),
    SelloSAT: z.string().optional(),
    CadenaOrigTFD: z.string().optional(),
    XmlBase64: z.string().optional(),
    PDF: z.string().optional(),
    XML: z.string().optional(),
});
//# sourceMappingURL=comprobante.js.map